import { useRouteError, useRevalidator } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@speakai/ui/button";
import { TriangleAlert, RefreshCw } from "lucide-react";
import { trackFailure } from "@/lib/integrations/sentry-tracking";
import { isChunkLoadError, CHUNK_RELOAD_SESSION_KEY } from "@/lib/utils/chunk-load";

/**
 * Dashboard-scoped error boundary — renders in the content area so the
 * sidebar stays alive. Used as errorElement on the pathless intermediate
 * route inside DashboardLayout.
 */
export function DashboardError() {
  const error = useRouteError();
  const revalidator = useRevalidator();
  const { t } = useTranslation();
  const [reloading, setReloading] = useState(false);

  const message = error instanceof Error ? error.message : undefined;
  const errorName = error instanceof Error ? error.name : undefined;
  const errorStack = error instanceof Error && error.stack ? error.stack.slice(0, 500) : undefined;
  const isChunkLoad = isChunkLoadError(error);

  useEffect(() => {
    if (!error) return;

    if (isChunkLoad) {
      let alreadyTried = false;
      try {
        alreadyTried = typeof sessionStorage !== "undefined" && !!sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY);
      } catch {
        alreadyTried = false;
      }
      if (!alreadyTried) {
        Sentry.addBreadcrumb({
          category: "navigation",
          level: "info",
          message: "chunk_load_auto_reload",
          data: { scope: "dashboard" },
        });
        try { sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, Date.now().toString()); } catch { /* ignore */ }
        setReloading(true);
        window.location.reload();
        return;
      }
    }

    Sentry.captureException(error);
    trackFailure(
      "navigation",
      "route_error",
      {
        url: typeof window !== "undefined" ? window.location.pathname : "",
        error_reason: message ?? String(error).slice(0, 200),
        error_name: errorName,
        error_stack: errorStack,
        scope: "dashboard",
      },
      // This boundary is shared by every dashboard route (home, explore, folder,
      // recorder, etc.); without a per-cause fingerprint, a stale-deploy chunk error
      // on one page and an unrelated crash on another merge into one issue with a
      // misleading culprit (see SPEAK-CLIENT-7E).
      ["dashboard", isChunkLoad ? "chunk_load_retry_exhausted" : (errorName ?? "UnknownError")],
    );
  }, [error, message, errorName, errorStack, isChunkLoad]);

  const handleTryAgain = () => {
    if (isChunkLoad) {
      try { sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY); } catch { /* ignore */ }
      window.location.reload();
      return;
    }
    revalidator.revalidate();
  };

  if (reloading) {
    return (
      <div className="flex flex-1 items-center justify-center p-6" role="status" aria-live="polite">
        <span className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-border border-t-primary" aria-hidden="true" />
        <span className="sr-only">{t("ACTION.RELOADING", "Reloading...")}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <div className="text-center max-w-md">
        <TriangleAlert aria-hidden="true" className="h-10 w-10 text-warning mx-auto" />
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          {isChunkLoad
            ? t("ERROR.NEW_VERSION_AVAILABLE", "A new version is available")
            : t("ERROR_NOTIFICATION.SOMETHING_WENT_WRONG")}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {isChunkLoad
            ? t("ERROR.RELOAD_TO_UPDATE", "Reload the page to load the latest version.")
            : t("ERROR_NOTIFICATION.PAGE_ERROR_DESC")}
        </p>
        {import.meta.env.DEV && message && (
          <p className="mt-2 text-xs text-muted-foreground font-mono bg-muted rounded px-3 py-2">
            {message}
          </p>
        )}
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button type="button" variant="outline" onClick={() => { window.location.href = "/home"; }}>
            {t("ACTION.GO_HOME")}
          </Button>
          <Button type="button" onClick={handleTryAgain}>
            <RefreshCw aria-hidden="true" className="h-3.5 w-3.5 mr-1.5" />
            {isChunkLoad ? t("ACTION.RELOAD", "Reload") : t("ACTION.TRY_AGAIN", "Try Again")}
          </Button>
        </div>
      </div>
    </div>
  );
}
